import * as moment from 'moment';
import { VariableBenefitAccountDetails } from '../../../../../models/transactions/variable-benefit-account-details';
import { VbRecurringPaymentInstruction } from '../../../../../models/transactions/vb-recurring-payment-instruction';
import { VariableBenefitPaymentFrequencyEnum } from '../../../../../models/enum/shared/variable-benefit-payment-frequency.enum';
import { VariableBenefitRecurringPaymentRow } from '../interfaces/variable-benefit-recurring-payment-row';
import {
  VbRecurringPaymentInstructionCard,
  VbRecurringPaymentInstructionType
} from '../interfaces/vb-recurring-payment-instruction-card';
import { StatusPill } from '../../../../../models/shared/status-pill';
import { VariableBenefitSummary } from '../../../../../models/submissions/variable-benefit-summary';

interface JurisdictionInstruction {
  jurisdictionName: string;
  instruction: VbRecurringPaymentInstruction;
}

const SUBTEXT_DATE_FORMAT = 'MMMM YYYY';

export class VbRecurringPaymentInstructionUtils {
  public static toCards(summary: VariableBenefitSummary): VbRecurringPaymentInstructionCard[] {
    const accountDetails = summary.accountDetails ?? [];
    const activeEntries = VbRecurringPaymentInstructionUtils.collectInstructions(
      accountDetails,
      VbRecurringPaymentInstructionType.Active
    );
    const scheduledEntries = VbRecurringPaymentInstructionUtils.collectInstructions(
      accountDetails,
      VbRecurringPaymentInstructionType.Scheduled
    );

    const activePayment = activeEntries[0]?.instruction ?? null;
    const scheduledPayment = scheduledEntries[0]?.instruction ?? null;

    const activeCard = VbRecurringPaymentInstructionUtils.buildCard(
      VbRecurringPaymentInstructionType.Active,
      activeEntries,
      summary.combinedActiveEstimatedAnnualRecurringPaymentAmount ?? 0,
      VbRecurringPaymentInstructionUtils.resolveActiveSubtext(activePayment, scheduledPayment)
    );
    const scheduledCard = VbRecurringPaymentInstructionUtils.buildCard(
      VbRecurringPaymentInstructionType.Scheduled,
      scheduledEntries,
      summary.combinedScheduledEstimatedAnnualRecurringPaymentAmount ?? 0,
      VbRecurringPaymentInstructionUtils.resolveScheduledSubtext(scheduledPayment)
    );

    const cards: VbRecurringPaymentInstructionCard[] = [];
    if (activeCard) {
      cards.push(activeCard);
    }
    if (scheduledCard) {
      cards.push(scheduledCard);
    }
    return cards;
  }

  private static collectInstructions(
    accountDetails: VariableBenefitAccountDetails[],
    type: VbRecurringPaymentInstructionType
  ): JurisdictionInstruction[] {
    return accountDetails
      .map(detail => {
        const instruction =
          type === VbRecurringPaymentInstructionType.Active
            ? detail.activeRecurringPaymentInstruction
            : detail.scheduledRecurringPaymentInstruction;
        if (!instruction) {
          return null;
        }
        return {
          jurisdictionName: detail.governingJurisdiction?.name ?? $localize`Non-Locked-In`,
          instruction
        };
      })
      .filter((entry): entry is JurisdictionInstruction => entry !== null)
      .sort((a, b) => a.jurisdictionName.localeCompare(b.jurisdictionName));
  }

  private static buildCard(
    instructionType: VbRecurringPaymentInstructionType,
    entries: JurisdictionInstruction[],
    annualTotal: number,
    subtext: string | undefined
  ): VbRecurringPaymentInstructionCard | null {
    if (entries.length === 0) {
      return null;
    }
    const isMonthly = entries[0].instruction.paymentFrequencyId === VariableBenefitPaymentFrequencyEnum.Monthly;
    const rows = entries.map(entry => VbRecurringPaymentInstructionUtils.toRow(entry));
    const total = rows.reduce((sum, r) => sum + r.amount, 0);
    const statusPill = VbRecurringPaymentInstructionUtils.getStatusPill(instructionType);
    return {
      instructionType,
      isMonthly,
      rows,
      total,
      statusPill,
      subtext,
      annualTotal
    };
  }

  private static toRow(entry: JurisdictionInstruction): VariableBenefitRecurringPaymentRow {
    return {
      jurisdictionName: entry.jurisdictionName,
      amount: entry.instruction.paymentAmount ?? 0,
      paymentTypeLabel: VbRecurringPaymentInstructionUtils.resolvePaymentTypeLabel(entry.instruction)
    };
  }

  private static resolvePaymentTypeLabel(instruction: VbRecurringPaymentInstruction): string {
    if (instruction.useMinimumAmount) {
      return $localize`Minimum`;
    }
    if (instruction.useMaximumAmount) {
      return $localize`Maximum`;
    }
    return $localize`Custom`;
  }

  private static getStatusPill(instructionType: VbRecurringPaymentInstructionType): StatusPill {
    if (instructionType === VbRecurringPaymentInstructionType.Scheduled) {
      return new StatusPill(0, $localize`Scheduled Change`, '#0D5D4B', '#E8F3F1');
    }
    return new StatusPill(0, $localize`Current Configuration`, '#024256', '#E6EEF1');
  }

  private static resolveActiveSubtext(
    active: VbRecurringPaymentInstruction | null,
    scheduled: VbRecurringPaymentInstruction | null
  ): string | undefined {
    if (!active) {
      return undefined;
    }
    const isMonthly = active.paymentFrequencyId === VariableBenefitPaymentFrequencyEnum.Monthly;

    if (scheduled) {
      return this.lastPaymentSubtext(active, isMonthly);
    }
    if (!isMonthly) {
      return this.paidInSubtext(active);
    }
    return undefined;
  }

  private static lastPaymentSubtext(active: VbRecurringPaymentInstruction, isMonthly: boolean): string | undefined {
    const referenceDate = isMonthly ? active.endDate : active.paymentStartDate;
    if (!referenceDate) {
      return undefined;
    }
    const lastPayment = isMonthly ? moment.utc(referenceDate).subtract(1, 'month') : moment.utc(referenceDate);
    return $localize`Last payment in ${lastPayment.format(SUBTEXT_DATE_FORMAT)}`;
  }

  private static paidInSubtext(active: VbRecurringPaymentInstruction): string | undefined {
    const paymentDate = moment.utc(active.paymentStartDate);
    if (!paymentDate.isBefore(moment.utc(), 'day')) {
      return undefined;
    }
    return $localize`Paid in ${paymentDate.format(SUBTEXT_DATE_FORMAT)}`;
  }

  private static resolveScheduledSubtext(scheduled: VbRecurringPaymentInstruction | null): string | undefined {
    if (!scheduled) {
      return undefined;
    }
    const startDate = moment.utc(scheduled.paymentStartDate);
    return $localize`Starting ${startDate.format(SUBTEXT_DATE_FORMAT)}`;
  }
}
